import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  phone: string;
  role: 'superadmin' | 'admin' | 'user';
  status: 'pending_approval' | 'active' | 'inactive';
  user_type: 'personal' | 'familiar' | 'comunidad' | 'empresa';
  tenant_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface TenantData {
  name: string;
  address?: string;
  description?: string;
  latitude?: string;
  longitude?: string;
}

interface AuthContextType {
  user: User | null;
  userProfile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    name: string,
    phone: string,
    userType: 'personal' | 'familiar' | 'comunidad' | 'empresa',
    tenantData?: TenantData
  ) => Promise<void>;
  signOut: () => Promise<void>;
  signInWithGoogle: (mode?: 'signin' | 'signup') => Promise<void>;
  refreshUserProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  // Función para obtener el perfil del usuario
  const fetchUserProfile = async (userId: string) => {
    try {
      console.log('Fetching user profile for:', userId);
      
      // Usar maybeSingle() en lugar de single() para evitar error 406 cuando no existe
      const { data, error } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (error) {
        console.error('Error fetching user profile:', error);
        setUserProfile(null);
        return;
      }

      // Si no existe el perfil, intentar crearlo desde auth.users
      if (!data) {
        console.log('User profile not found, fetching from auth...');
        const { data: { user: authUser } } = await supabase.auth.getUser();
        
        if (authUser) {
          // Determinar el rol basado en el email o metadata
          // Si es el primer usuario o tiene email específico, puede ser superadmin
          let userRole: 'superadmin' | 'admin' | 'user' = 'user';
          let userStatus: 'pending_approval' | 'active' | 'inactive' = 'pending_approval';
          
          // Verificar si es el usuario que convertimos a superadmin
          if (authUser.email === 'haliagav@gmail.com') {
            userRole = 'superadmin';
            userStatus = 'active';
          }
          
          // Primero intentar obtener el perfil por si acaso existe (race condition)
          const { data: existingCheck } = await supabase
            .from('users')
            .select('*')
            .eq('id', userId)
            .maybeSingle();
          
          if (existingCheck) {
            console.log('User profile found after recheck:', existingCheck);
            // Si es superadmin pero está inactivo, corregir el estado
            if (existingCheck.role === 'superadmin' && existingCheck.status !== 'active') {
              const { data: updatedProfile } = await supabase
                .from('users')
                .update({ status: 'active', is_active: true })
                .eq('id', userId)
                .select()
                .maybeSingle();
              if (updatedProfile) {
                setUserProfile(updatedProfile as UserProfile);
                return;
              }
            }
            setUserProfile(existingCheck as UserProfile);
            return;
          }
          
          // Si realmente no existe, crearlo
          const { data: newProfile, error: createError } = await supabase
            .from('users')
            .insert({
              id: authUser.id,
              email: authUser.email?.toLowerCase().trim() || '',
              name: authUser.user_metadata?.name || 
                    authUser.user_metadata?.full_name || 
                    authUser.email?.split('@')[0] || 
                    'User',
              phone: authUser.user_metadata?.phone || '',
              auth_provider: authUser.app_metadata?.provider === 'google' ? 'google' : 'local',
              role: userRole,
              status: userStatus,
            })
            .select()
            .maybeSingle();

          if (createError) {
            console.error('Error creating user profile:', createError);
            // Si el error es que ya existe (duplicate key), obtener el perfil existente
            if (createError.code === '23505' || createError.message?.includes('duplicate key')) {
              console.log('User profile already exists (duplicate key), fetching...');
              const { data: existingProfile, error: fetchError } = await supabase
                .from('users')
                .select('*')
                .eq('id', userId)
                .maybeSingle();
              
              if (fetchError) {
                console.error('Error fetching existing profile:', fetchError);
                setUserProfile(null);
                return;
              }
              
              if (existingProfile) {
                console.log('Existing user profile found:', existingProfile);
                // Si es superadmin pero está inactivo, corregir el estado
                if (existingProfile.role === 'superadmin' && existingProfile.status !== 'active') {
                  const { data: updatedProfile } = await supabase
                    .from('users')
                    .update({ status: 'active', is_active: true })
                    .eq('id', userId)
                    .select()
                    .maybeSingle();
                  if (updatedProfile) {
                    setUserProfile(updatedProfile as UserProfile);
                    return;
                  }
                }
                setUserProfile(existingProfile as UserProfile);
                return;
              }
            }
            setUserProfile(null);
            return;
          }

          if (newProfile) {
            console.log('User profile created:', newProfile);
            setUserProfile(newProfile as UserProfile);
            return;
          }
        }
        
        setUserProfile(null);
        return;
      }

      // Si el usuario es superadmin pero está inactivo, corregir el estado
      if (data.role === 'superadmin' && data.status !== 'active') {
        console.warn('Superadmin con estado incorrecto, corrigiendo...', data);
        const { data: updatedProfile, error: updateError } = await supabase
          .from('users')
          .update({ status: 'active', is_active: true })
          .eq('id', userId)
          .select()
          .maybeSingle();
        
        if (!updateError && updatedProfile) {
          console.log('Estado del superadmin corregido:', updatedProfile);
          setUserProfile(updatedProfile as UserProfile);
          return;
        }
      }

      console.log('User profile fetched:', data);
      setUserProfile(data as UserProfile);
    } catch (error) {
      console.error('Error in fetchUserProfile:', error);
      setUserProfile(null);
    }
  };

  const refreshUserProfile = async () => {
    if (user) {
      await fetchUserProfile(user.id);
    }
  };

  useEffect(() => {
    // Obtener sesión inicial
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        await fetchUserProfile(session.user.id);
      }
      
      setLoading(false);
    });

    // Escuchar cambios de autenticación
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange(async (_event, session) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user) {
        // Cuando el usuario inicia sesión, obtener su perfil
        await fetchUserProfile(session.user.id);
      } else {
        setUserProfile(null);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // Suscribirse a cambios en tiempo real del perfil del usuario
  useEffect(() => {
    if (!user?.id) return;

    const channel = supabase
      .channel(`user-profile-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'users',
          filter: `id=eq.${user.id}`,
        },
        (payload) => {
          console.log('User profile updated via Realtime:', payload.new);
          // Actualizar el perfil del usuario cuando cambia en la base de datos
          setUserProfile(payload.new as UserProfile);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    if (error) throw error;
  };

  const signUp = async (
    email: string,
    password: string,
    name: string,
    phone: string,
    userType: 'personal' | 'familiar' | 'comunidad' | 'empresa',
    tenantData?: TenantData
  ) => {
    // Registrar en Supabase Auth
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: {
          name,
          phone,
          user_type: userType,
        },
      },
    });

    if (authError) throw authError;

    // Si el usuario se creó, crear perfil en la tabla users
    if (authData.user) {
      // Determinar rol según tipo de usuario
      let role: 'superadmin' | 'admin' | 'user' = 'user';
      if (userType === 'familiar' || userType === 'comunidad' || userType === 'empresa') {
        role = 'admin';
      }

      // Crear perfil de usuario
      const { error: profileError } = await supabase
        .from('users')
        .insert({
          id: authData.user.id,
          email: email.toLowerCase().trim(),
          name,
          phone: phone || '',
          auth_provider: 'local',
          user_type: userType,
          role,
          status: 'pending_approval',
        });

      if (profileError) {
        console.error('Error creating user profile:', profileError);
        throw new Error('Error al crear el perfil de usuario: ' + profileError.message);
      }

      // Si hay datos de tenant y el tipo no es personal, crear tenant y asociarlo
      if (tenantData && userType !== 'personal' && tenantData.name) {
        try {
          // Crear tenant
          const { data: newTenant, error: tenantError } = await supabase
            .from('tenants')
            .insert({
              uuid: crypto.randomUUID(),
              name: tenantData.name,
              address: tenantData.address || null,
              description: tenantData.description || null,
              type: userType, // El tipo del tenant coincide con el tipo del usuario
              latitude: tenantData.latitude || null,
              longitude: tenantData.longitude || null,
              status: 'active',
              is_active: true,
            })
            .select()
            .single();

          if (tenantError) {
            console.error('Error creating tenant:', tenantError);
            throw new Error('Error al crear el tenant: ' + tenantError.message);
          }

          if (newTenant) {
            // Crear relación usuario-tenant
            const { error: relationError } = await supabase
              .from('user_tenants')
              .insert({
                user_id: authData.user.id,
                tenant_id: newTenant.id,
              });

            if (relationError) {
              console.error('Error creating user-tenant relation:', relationError);
              // No fallar completamente, pero loguear el error
              // El usuario puede asociarse manualmente después
            } else {
              // Actualizar también el campo legacy tenant_id para compatibilidad
              await supabase
                .from('users')
                .update({ tenant_id: newTenant.id })
                .eq('id', authData.user.id);
            }
          }
        } catch (tenantError: any) {
          console.error('Error in tenant creation process:', tenantError);
          // No fallar completamente si falla la creación del tenant
          // El usuario puede continuar y el admin puede crear el tenant manualmente
        }
      }
    }
  };

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  const signInWithGoogle = async (mode: 'signin' | 'signup' = 'signin') => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location.origin}/auth/callback?mode=${mode}`,
      },
    });

    if (error) throw error;
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        userProfile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        signInWithGoogle,
        refreshUserProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
